import { useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CalendarIcon } from '../../assets';
import {
  CREATE_BRAND_EVENT,
  UPDATE_BRAND_EVENT,
} from '../../graphql/brandActivity/mutation';
import { useNotification } from '../../hooks/useNotification';
import { Button } from '../common/Button';
import { Error } from '../common/Error';
import { DatePickerInput } from '../inputs/DatePicker';
import { GenericDropdown } from '../inputs/GenericDropdown';
import { TextInput } from '../inputs/TextInput';
import { PopupLayout } from '../popups/PopupLayout';

type BrandEventPopupProps = {
  brandId: string;
  onClose: () => void;
  onSaved: () => void;
  eventId?: string;
  defaultValues?: {
    name?: string;
    type?: string;
    scheduledAt?: string;
    notes?: string;
  };
};

type TEventForm = {
  name: string;
  scheduledDate: string;
  scheduledTime: string;
  type: string;
  notes: string;
};

const normalizeDateValue = (dateVal?: string | number | Date | null) => {
  if (dateVal === null || dateVal === undefined || dateVal === '') return null;

  if (dateVal instanceof Date) return dayjs(dateVal);

  if (typeof dateVal === 'number') {
    return dayjs(dateVal < 1e12 ? dateVal * 1000 : dateVal);
  }

  const value = String(dateVal).trim();
  if (/^\d+$/.test(value)) {
    const numeric = Number(value);
    return dayjs(numeric < 1e12 ? numeric * 1000 : numeric);
  }

  return dayjs(value);
};

export const CreateBrandEvent = ({
  brandId,
  onClose,
  onSaved,
  eventId,
  defaultValues,
}: BrandEventPopupProps) => {
  const { t } = useTranslation('common');
  const { notifySaveSuccess, notifySaveError } = useNotification();
  const isEdit = !!eventId;
  const parsedDefaultScheduledAt = normalizeDateValue(
    defaultValues?.scheduledAt,
  );

  const initialDate = parsedDefaultScheduledAt?.isValid()
    ? parsedDefaultScheduledAt.format('MM/DD/YYYY')
    : '';
  const initialTime = parsedDefaultScheduledAt?.isValid()
    ? parsedDefaultScheduledAt.format('HH:mm')
    : '';

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<TEventForm>({
    defaultValues: {
      name: defaultValues?.name || '',
      type: defaultValues?.type || 'phone_call',
      scheduledDate: initialDate,
      scheduledTime: initialTime,
      notes: defaultValues?.notes || '',
    },
  });

  const [createEvent, { loading: loadingCreate }] =
    useMutation(CREATE_BRAND_EVENT);
  const [updateEvent, { loading: loadingUpdate }] =
    useMutation(UPDATE_BRAND_EVENT);

  const eventTypeOptions = [
    { value: 'phone_call', label: t('eventTypes.phone_call') },
    { value: 'google_meet', label: t('eventTypes.google_meet') },
    { value: 'in_person_meeting', label: t('eventTypes.in_person_meeting') },
  ];

  const onSubmit = (data: TEventForm) => {
    const parsed = dayjs(data.scheduledDate, 'MM/DD/YYYY');
    if (!parsed.isValid()) {
      notifySaveError();
      return;
    }

    const [hours, minutes] = data.scheduledTime.split(':');
    const hh = String(parseInt(hours, 10)).padStart(2, '0');
    const mm = String(parseInt(minutes, 10)).padStart(2, '0');
    const scheduledAt = `${parsed.format('YYYY-MM-DD')}T${hh}:${mm}:00`;

    if (isEdit) {
      updateEvent({
        variables: {
          eventId,
          name: data.name,
          type: data.type,
          scheduledAt,
          notes: data.notes || undefined,
        },
        onCompleted: () => {
          notifySaveSuccess();
          onSaved();
        },
        onError: () => notifySaveError(),
      });
    } else {
      createEvent({
        variables: {
          brandId,
          name: data.name,
          type: data.type,
          scheduledAt,
          notes: data.notes || undefined,
        },
        onCompleted: () => {
          notifySaveSuccess();
          onSaved();
        },
        onError: () => notifySaveError(),
      });
    }
  };

  return (
    <PopupLayout
      title={
        isEdit ? t('activity.editEventTitle') : t('activity.createEventTitle')
      }
      closeModal={onClose}
      icon={<CalendarIcon className="size-[20px]" />}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-4 mt-4"
      >
        <TextInput
          register={register}
          registerName="name"
          type="text"
          label={t('activity.eventName')}
          placeholder={t('activity.eventNamePlaceholder')}
          validation={{ required: t('global.requiredField') }}
          errors={errors.name}
        />
        <div className="flex flex-row gap-4">
          <DatePickerInput
            control={control}
            name="scheduledDate"
            label={t('activity.eventDate')}
            placeholder={t('global.placeholderDateInput')}
            fullSpace
            validation={{ required: t('global.requiredField') }}
            errors={errors.scheduledDate}
          />
          <div className="w-full">
            <label className="my-2 block text-[14px] font-[600]">
              {t('activity.eventTime')}
            </label>
            <input
              type="time"
              className={`block h-[41px] w-full py-[8px] text-sm px-3 font-medium text-dark-blue placeholder:text-[#5A5A5A] focus:outline-none focus:ring-0 rounded-[4px] focus-visible:outline-none ${errors.scheduledTime ? 'border border-red-600' : ''}`}
              {...register('scheduledTime', {
                required: t('global.requiredField'),
              })}
            />
            <div className="mt-1">
              {errors.scheduledTime?.message && (
                <Error error={errors.scheduledTime.message} />
              )}
            </div>
          </div>
        </div>
        <GenericDropdown
          control={control}
          name="type"
          label={t('activity.eventType')}
          placeholder={t('activity.eventType')}
          options={eventTypeOptions}
          isClearable={false}
          validation={{ required: t('global.requiredField') }}
        />
        <div className="w-full">
          <label className="my-2 block text-[14px] font-[600]">
            {t('activity.eventNotes')}
          </label>
          <textarea
            className="w-full h-[100px] px-[5px] py-[2px] rounded-[5px] border-none outline-none text-sm text-dark-blue"
            placeholder={t('activity.eventNotesPlaceholder')}
            {...register('notes')}
          />
        </div>
        <div className="flex justify-end">
          <Button
            text={t('global.save')}
            style="light-blue"
            type="submit"
            loading={loadingCreate || loadingUpdate}
            customClassnames="!px-14"
          />
        </div>
      </form>
    </PopupLayout>
  );
};
